import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';

import { routeNaming, AppRedirect } from '../../../routes';
import { enforceMenus } from '../../../hocs/enforce-menus';
import { MenuStore } from '../../../stores/MenuStore';
import { Spinner } from '../../../common/spinner';
import { ProductCard } from '../../../common/product-card';
import { CategoryBanner } from '../category-banner';
import { CategoryProductsStore } from '../stores/CategoryProductsStore';
import { Filters } from '../filters';
import styles from './MainBody.css';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import { IntraSearchFilter } from '../intra-search-filter';
import { SEO } from '../../../common/seo/Seo';
import { GA4 } from '../../../helpers/GA4';

const IPropTypes = {
  productTypeId: PropTypes.string,
  onProductTypeUpdate: PropTypes.func,
  subMenuId: PropTypes.string.isRequired,
};

const defaultProps = {
  onProductTypeUpdate: () => {},
  productTypeId: null,
};

const DEFAULT_INTRA_FILTER = 3;

const renderRedirect = () => (
  <AppRedirect
    routeName={routeNaming.NOT_FOUND}
  />
);

const findSubMenu = (menus, subMenuId) => {
  let subMenu = null;
  menus.forEach((menu) => {
    const possibleSubMenu = menu.subMenus.find(sMenu => sMenu.id === subMenuId);
    if (possibleSubMenu && !subMenu) {
      subMenu = possibleSubMenu;
    }
  });
  return subMenu;
};

class MainBody extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: null,
    };

    this._onFilterChange = this._onFilterChange.bind(this);
  }

  componentDidMount() {
    if (ConfigParametersStore.showIntranet) {
      CategoryProductsStore.fetchProductsForCategory(
        this.props.subMenuId,
        this.props.productTypeId,
        DEFAULT_INTRA_FILTER,
      );
    } else {
      CategoryProductsStore.fetchProductsForCategory(
        this.props.subMenuId,
        this.props.productTypeId,
      );
      this.disposer = reaction(
        () => CategoryProductsStore.products,
        (newProducts) => {
          if (!newProducts) {
            return;
          }
          GA4.viewItemList(newProducts.map(x => ({
            item_id: x.id,
            item_name: x.name.trim(),
            price: x.financing && x.financing.price,
          })));
        },
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.subMenuId !== this.props.subMenuId ||
        prevProps.productTypeId !== this.props.productTypeId ||
        prevState.filter !== this.state.filter) {
      CategoryProductsStore.fetchProductsForCategory(
        this.props.subMenuId,
        this.props.productTypeId,
        this.state.filter,
      );
    }
  }

  _onFilterChange(filter) {
    this.setState({
      filter,
    });
  }

  render() {
    const { products } = CategoryProductsStore;
    // If the selected subMenu doesn't exist, redirect to not found page.
    const currentSubMenu = findSubMenu(MenuStore.menus, this.props.subMenuId);
    if (!currentSubMenu) {
      return renderRedirect();
    }
    let currentProductType = null;
    if (this.props.productTypeId) {
      currentProductType = currentSubMenu.productTypes.find(pType => (
        pType.id === this.props.productTypeId
      ));
    }
    const containerStyle = currentSubMenu.image === null
      ? styles.containerWithoutImage
      : null;

    const { intraSearchFilters } = ConfigParametersStore.parameters;

    return (
      <div>
        <SEO
          title={`Productos de ${currentSubMenu.name}`}
          description={`Explora nuestra amplia selección de productos en
            la categoría de ${currentSubMenu.name} en Carlos Gutiérrez S.A.. Desde
            opciones populares hasta artículos exclusivos, aquí encontrarás
            todo lo que buscas con la mejor calidad y al mejor precio.
            Filtra por características, marcas, precios y más para encontrar
            el producto perfecto que se ajuste a tus necesidades.
            Disfruta de una experiencia de compra fácil, segura y conveniente
            mientras navegas por nuestra variada oferta. ¡Haz tu compra hoy y
            descubre las mejores opciones en Carlos Gutiérrez S.A.!`}
          type="category"
          image={currentSubMenu.image}
          keyword={currentSubMenu.name}
        />
        <CategoryBanner
          imageSrc={currentSubMenu.image}
          text={currentSubMenu.name}
          textColor={currentSubMenu.nameColor}
        />
        <div className={[styles.container, containerStyle].join(' ')}>
          <Filters
            className={styles.filters}
            onTypeSelect={this.props.onProductTypeUpdate}
            productTypes={currentSubMenu.productTypes}
            selectedTypeId={this.props.productTypeId}
          />
          <div className={[styles.sectionContainer,
            currentProductType ? styles.withCategoryName : styles.withoutCategoryName].join(' ')}
          >
            {
              !!currentProductType &&
              <div>
                <h3 className={styles.productTypeName}>
                  {currentProductType.name}
                </h3>
                <p className={styles.productTypeDescription}>
                  {currentProductType.description}
                </p>
              </div>
            }
            {
              ConfigParametersStore.showIntranet &&
              <div className={styles.sortContainer}>
                <p className={styles.orderBy}>
                  Filtrar por:
                </p>
                <IntraSearchFilter
                  onFilterChange={this._onFilterChange}
                  filterOptions={intraSearchFilters}
                  selectedFilter={this.state.filter || DEFAULT_INTRA_FILTER}
                />
              </div>
            }
          </div>
          {
            products === null
            ? <Spinner className={styles.spinner} imgClassName={styles.spinnerImage} />
            : (
              <div className={styles.productList}>
                {products.map(product => (
                  <ProductCard
                    className={styles.productCard}
                    key={product.id}
                    product={product}
                  />
                ))}
              </div>
            )
          }
        </div>
      </div>
    );
  }
}

MainBody.propTypes = IPropTypes;
MainBody.defaultProps = defaultProps;

const WrappedMainBody = enforceParameters(enforceMenus(observer(MainBody)));

export { WrappedMainBody as MainBody };



// WEBPACK FOOTER //
// ./src/pages/product-index/main-body/MainBody.jsx